import type {CreateExtArgs, CreateExtensionArgument, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    BuilderPresets,
    BuilderStyle,
    CompRef,
    Experiment,
    InnerElementPath,
    NestedDataPathSegment,
    OverrideOrDefaultData,
    Pointer
} from '@wix/document-services-types'
import type {
    Archetype,
    CssCustomPropertyItem,
    CssPropertyEnumCssPropertyType,
    CssPropertyItem,
    PresetInnerElementDefaults,
    ContentFill,
    SizingType,
    ComponentInitialSize
} from '@wix/ambassador-devcenter-v1-component-type-data/types'
import type {PlatformExtensionAPI} from '../platform'
import type {StructureExtensionAPI} from '../structure'
import type {InnerElementsExtensionAPI} from './innerElements'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {PresetsExtensionAPI} from '../presets'
import {
    ARCHETYPE,
    type CssCustomProperties,
    type CssProperties,
    type EditorReactComponent,
    type ElementItem,
    LAYOUT
    // eslint-disable-next-line wix-editor/no-internal-import
} from '@wix/component-protocol/schema'
import _ from 'lodash'
import {ReportableError} from '@wix/document-manager-utils'
import {MAIN_TEXT_FIELD_MAPPING} from './mainTextFieldMapping'
import {getManifestItems as getManifestItemsFromMeta} from './utils'
import type {SchemaExtensionAPI} from '../schema/schema'

export type GetManifestItemsOptions = {
    [key in SupportedManifestSpaces]?: {types?: {prop: string; extract?(data: any): any}[]}
}
export type GetManifestItemsResult = {
    [key in SupportedManifestSpaces]?: OverrideOrDefaultData[]
}

type SupportedManifestSpaces = 'data' | 'style'

interface ManifestApi extends ExtensionAPI {
    getArchType(componentType: string): Archetype
    getDisplayName(componentType: string): string
    getManifestItemsFromSeveralSpaces(
        compPointer: Pointer,
        options: GetManifestItemsOptions,
        withInnerElements: boolean
    ): GetManifestItemsResult
    getData(
        compPointer: Pointer,
        withInnerElements: boolean,
        types?: {prop: string; extract?(data: any): any}[]
    ): OverrideOrDefaultData[]
    getStyle(
        compPointer: Pointer,
        withInnerElements: boolean,
        types?: {prop: string; extract?(data: any): any}[]
    ): OverrideOrDefaultData[]
    innerElements: {
        buildTraversableFlatMap(compPointer: CompRef): Record<string, {innerElements: string[]}>
        getInnerElementSlotType(compRef: CompRef, innerElementPath: InnerElementPath, slotKeyName: string): string
    }
    cssStyle: {
        getDefaultDefinition(
            compPointer: CompRef,
            innerElementsPath?: string[]
        ): Readonly<Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'>>
        getCustomCssPropertiesRoles(
            compPointer: CompRef,
            innerElementsPath?: string[]
        ): Readonly<Record<string, CssPropertyEnumCssPropertyType>>
    }
    validateContainerTypeForPopulation(
        slottedComponent: Pointer,
        slotName: string,
        nestedDataPath?: NestedDataPathSegment[],
        innerElementPath?: InnerElementPath
    ): void
    validateContainerTypeForRemove(
        slottedComponent: Pointer,
        slotName: string,
        nestedDataPath?: NestedDataPathSegment[],
        innerElementPath?: InnerElementPath
    ): void
    layout: {
        isContentHeight(compPointer: CompRef): boolean
        isContentWidth(compPointer: CompRef): boolean
        isStretchedHeight(compPointer: CompRef): boolean
        isStretchedWidth(compPointer: CompRef): boolean
        shouldPreserveAspectRatio(compPointer: CompRef): boolean
        getContentFill(compPointer: CompRef): ContentFill | undefined
        getInitialSize(compPointer: CompRef): ComponentInitialSize | undefined
    }
    plugins: {
        hasOverlappingContainerPlugin(compPointer: CompRef): boolean
    }
    getMainTextField(compType: string, data: any): string | undefined
}

interface ManifestExtensionAPI extends ExtensionAPI {
    manifest: ManifestApi
}

type ExtractDataFunction = (
    override: any,
    manifestData: Record<string, any>,
    presetData: any,
    types: {prop: string; extract?(data: any): any}[] | undefined,
    isInnerElement: boolean,
    experimentInstance?: Experiment
) => OverrideOrDefaultData[]

const dataExtractDataFunction = (
    override: any,
    manifestData: Record<string, any>,
    typeNameInManifest: string,
    whereToWriteInDM: string,
    presetData: any,
    types: {prop: string; extract?(data: any): any}[] | undefined,
    isInnerElement: boolean | undefined,
    nestedDataPath: NestedDataPathSegment[] = []
) => {
    const getValue = (prop: string, overrideOrDefault: any, meta: any, defaultValue?: any) => {
        return overrideOrDefault?.[prop] ?? meta?.defaultValue ?? defaultValue
    }

    const getId = (overrideOrDefault: any, key: string, index?: number) => {
        const idField = '_id'
        const overrideOfProp = overrideOrDefault?.[key]
        return index !== undefined ? overrideOfProp?.[index]?.[idField] : overrideOfProp?.[idField]
    }

    const buildNestedPath = (
        nestedPath: NestedDataPathSegment[],
        type: 'array' | 'simple',
        key: string,
        overrideOrDefault: any,
        index?: number
    ): NestedDataPathSegment[] => {
        return [
            ...nestedPath,
            {
                type,
                key,
                _id: getId(overrideOrDefault, key, index)
            }
        ]
    }

    const extractNestedTypesData = (
        prop: string,
        meta: any,
        overrideOrDefault: any,
        presetDataNested: any,
        nestedPath: NestedDataPathSegment[]
    ): OverrideOrDefaultData[] => {
        const nestedResults: OverrideOrDefaultData[] = []
        const type = meta[typeNameInManifest]

        if (type === 'arrayItems') {
            const manifestItems = getManifestItemsFromMeta(meta.arrayItems)
            if (_.isEmpty(manifestItems)) return []
            const arrayValue = getValue(prop, overrideOrDefault, meta, [])
            const arrayPresetData = presetDataNested?.[prop] ?? presetDataNested?.[typeNameInManifest]?.[prop]

            if (Array.isArray(arrayValue)) {
                arrayValue.forEach((arrayItem: any, index: number) => {
                    const currentNestedPath = buildNestedPath(nestedPath, 'array', prop, overrideOrDefault, index)
                    const arrayItemPresetData = arrayPresetData?.[index] ?? arrayPresetData?.defaultValue?.[index]
                    const nestedResultsForItem = dataExtractDataFunction(
                        arrayItem,
                        manifestItems,
                        typeNameInManifest,
                        whereToWriteInDM,
                        arrayItemPresetData,
                        types,
                        isInnerElement,
                        currentNestedPath
                    )
                    nestedResults.push(...nestedResultsForItem)
                })
            }
        }

        if (type === 'data' && meta.data?.items) {
            const nestedDataValue = getValue(prop, overrideOrDefault, meta, {})
            const currentNestedPath = buildNestedPath(nestedPath, 'simple', prop, overrideOrDefault)
            const nestedPresetData = presetDataNested?.[prop] ?? presetDataNested?.data?.[prop]
            const nestedResultsForData = dataExtractDataFunction(
                nestedDataValue,
                meta.data.items,
                typeNameInManifest,
                whereToWriteInDM,
                nestedPresetData,
                types,
                isInnerElement,
                currentNestedPath
            )
            nestedResults.push(...nestedResultsForData)
        }

        return nestedResults
    }

    const overrideOrDefaultDataArr: OverrideOrDefaultData[] = []
    Object.entries(manifestData).forEach(([prop, meta]: [string, any]) => {
        const type = meta[typeNameInManifest]
        const extractionDetails = types?.find(toExtractType => toExtractType.prop === type)

        if (type === 'arrayItems' || type === 'data') {
            const nestedResults = extractNestedTypesData(prop, meta, override, presetData, nestedDataPath)
            overrideOrDefaultDataArr.push(...nestedResults)
        }

        if (!types || extractionDetails) {
            const defaultValue = presetData?.[typeNameInManifest]?.[prop]?.defaultValue ?? meta?.defaultValue
            const value = getValue(prop, override, meta, defaultValue)
            overrideOrDefaultDataArr.push({
                prop,
                value: extractionDetails?.extract ? extractionDetails.extract(value) : value,
                type,
                whereToWriteInDM,
                metaData: meta[type] ?? {},
                nestedDataPath
            })
        }
    })
    return overrideOrDefaultDataArr
}

const typeToExtractDataFunction: Record<string, ExtractDataFunction> = {
    data: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined,
        isInnerElement: boolean
    ) => dataExtractDataFunction(override, manifestData, 'dataType', 'data', presetData, types, isInnerElement),
    cssProperties: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => {
        const whereToWriteInDM = 'cssProperties'
        if (!types) {
            return Object.entries(manifestData).map(([prop, meta]: [string, any]) => {
                const defaultValue = presetData?.[whereToWriteInDM]?.[prop]?.defaultValue ?? meta?.defaultValue
                const value = override[whereToWriteInDM]?.[prop] ?? defaultValue
                return {
                    value,
                    whereToWriteInDM,
                    prop,
                    type: prop,
                    metaData: meta?.[prop] ?? {}
                }
            })
        }
        return types
            .map(typeToExtract => {
                const {prop, extract} = typeToExtract
                const defaultValue =
                    presetData?.[whereToWriteInDM]?.[prop]?.defaultValue ?? manifestData?.[prop]?.defaultValue
                const value = override[whereToWriteInDM]?.[prop] ?? defaultValue
                const meta = manifestData?.[prop]
                return {
                    value: extract ? extract(value) : value,
                    whereToWriteInDM,
                    prop,
                    type: prop,
                    metaData: meta?.[prop] ?? {}
                }
            })
            .filter(context => context.value !== undefined)
    },
    cssCustomProperties: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => {
        const overrideOrDefaultDataArr: OverrideOrDefaultData[] = []
        Object.entries(manifestData).forEach(([prop, meta]: [string, any]) => {
            const type = meta.cssPropertyType
            const extractionDetails = types?.find(toExtractType => toExtractType.prop === type)
            if (!types || extractionDetails) {
                const defaultValue = presetData?.cssPropertyType?.[prop]?.defaultValue ?? meta?.defaultValue
                const value = override?.cssCustomProperties?.[prop] ?? defaultValue
                overrideOrDefaultDataArr.push({
                    prop,
                    value: extractionDetails?.extract ? extractionDetails.extract(value) : value,
                    type,
                    whereToWriteInDM: 'cssCustomProperties',
                    metaData: meta[type] ?? {}
                })
            }
        })
        return overrideOrDefaultDataArr
    }
}

type ManifestSliceGetter = (manifestData: Record<string, any>) => Record<string, any> | undefined

const EXTRACT_CONFIG: Record<
    keyof GetManifestItemsOptions,
    {
        extractors: {getManifest: ManifestSliceGetter; fn: ExtractDataFunction}[]
        getOverride(extensionAPI: ExtensionAPI, compPointer: CompRef, path: InnerElementPath): any
    }
> = {
    data: {
        extractors: [{getManifest: m => m?.data, fn: typeToExtractDataFunction.data}],
        getOverride: (extensionAPI: ExtensionAPI, compPointer: CompRef, path: InnerElementPath) => {
            if (_.isEmpty(path))
                return (
                    (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
                        compPointer,
                        'data'
                    ) ?? {}
                )
            return (
                (extensionAPI as InnerElementsExtensionAPI).innerElements.getInnerElementData(compPointer, path) ?? {}
            )
        }
    },
    style: {
        extractors: [
            {getManifest: m => m?.cssProperties ?? {}, fn: typeToExtractDataFunction.cssProperties},
            {getManifest: m => m?.cssCustomProperties ?? {}, fn: typeToExtractDataFunction.cssCustomProperties}
        ],
        getOverride: (extensionAPI: ExtensionAPI, compPointer: CompRef, path: InnerElementPath) => {
            if (_.isEmpty(path))
                return (
                    (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
                        compPointer,
                        'style'
                    ) ?? {}
                )
            return (
                (extensionAPI as InnerElementsExtensionAPI).innerElements.getInnerElementStyle(compPointer, path) ?? {}
            )
        }
    }
}

const flattenPresets = (compPresets: any): Record<string, any> => {
    if (!compPresets) return {}
    return Object.fromEntries(
        Object.entries(compPresets).map(([name, data]: [string, any]) => [
            name,
            data?.elements || data?.presetDefaults?.elements
        ])
    )
}

const extractManifestItemsFromOverrides = (
    opts: GetManifestItemsOptions,
    manifestData: Record<string, any> | undefined,
    overrides: Partial<Record<keyof GetManifestItemsOptions, any>>,
    presetData: any,
    path?: string[]
): GetManifestItemsResult => {
    if (!manifestData) return {}
    const result: GetManifestItemsResult = {}
    const keys: SupportedManifestSpaces[] = Object.keys(opts) as SupportedManifestSpaces[]
    if (keys.length === 0) return result

    for (const key of keys) {
        result[key] = []
        const config = EXTRACT_CONFIG[key]
        if (!config) continue
        const types = opts[key]?.types
        for (const {getManifest, fn} of config.extractors) {
            const manifestSlice = getManifest(manifestData ?? {})
            if (manifestSlice === undefined) continue
            const items = fn(overrides[key], manifestSlice, presetData, types, !_.isEmpty(path))
            const itemsWithPath = path && path.length > 0 ? items.map(item => ({...item, path})) : items
            ;(result[key] ??= []).push(...itemsWithPath)
        }
    }
    return result
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    return {
        name: 'manifest',
        dependencies: new Set(['platform', 'structure', 'innerElements', 'variants', 'presets']),
        createExtensionAPI(extArgs: CreateExtArgs): ManifestExtensionAPI {
            const {extensionAPI, dal, pointers} = extArgs
            const {platform} = extensionAPI as PlatformExtensionAPI
            const {components} = extensionAPI as StructureExtensionAPI
            const {schemaAPI} = extensionAPI as SchemaExtensionAPI

            const getComponentDetailsFromCSM = (componentType: string): any => {
                const builderCompData = platform.getBuilderCompAppData(componentType)
                if (!builderCompData) {
                    return undefined
                }
                const {appDefinitionId, componentId} = builderCompData
                const appPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
                const appData = dal.get(appPointer)
                const componentEntry = appData?.components?.find((comp: any) => comp.componentId === componentId)

                if (!componentEntry) {
                    throw new ReportableError({
                        errorType: 'getComponentDetailsFromCSM_componentNotAvailable',
                        message: 'Component not available - app may not be fully installed',
                        extras: {
                            componentType,
                            appDefinitionId,
                            componentId,
                            appDataExists: !!appData,
                            componentsExist: !!appData?.components
                        }
                    })
                }

                return componentEntry.data
            }

            const createInnerElementsWalker = (opts: GetManifestItemsOptions) => {
                const walk = (
                    componentPointer: Pointer,
                    elementsTree: Record<string, any>,
                    relativePath: string[],
                    compPresetsForLevel: any,
                    result: GetManifestItemsResult
                ): void => {
                    if (!elementsTree) return
                    const optsKeys = Object.keys(opts) as SupportedManifestSpaces[]
                    Object.entries(elementsTree).forEach(([elem, val]: [string, any]) => {
                        const path = [...relativePath, elem]
                        const resolved = val.inlineElement ?? val.refElement?.resolvedElement ?? val

                        const overrides = Object.fromEntries(
                            optsKeys.map(key => [
                                key,
                                EXTRACT_CONFIG[key].getOverride(extensionAPI, componentPointer as CompRef, path)
                            ])
                        )
                        const innerPreset =
                            (extensionAPI as InnerElementsExtensionAPI).innerElements.getInnerElementPresets(
                                componentPointer as CompRef,
                                path
                            ) || (extensionAPI as PresetsExtensionAPI).presets.get(componentPointer as CompRef)
                        const presetData = innerPreset && compPresetsForLevel?.[innerPreset.presetName]?.[elem]
                        const extracted = extractManifestItemsFromOverrides(opts, resolved, overrides, presetData, path)
                        for (const key of Object.keys(extracted) as SupportedManifestSpaces[]) {
                            const value = extracted[key] ?? []
                            if (!result[key]) result[key] = []
                            result[key]!.push(...value)
                        }

                        walk(
                            componentPointer,
                            resolved?.elements,
                            [...relativePath, elem],
                            flattenPresets(compPresetsForLevel),
                            result
                        )
                    })
                }
                return walk
            }

            const getManifestItemsInternal = (
                compPointer: CompRef,
                withInnerElements: boolean,
                options: GetManifestItemsOptions
            ): GetManifestItemsResult => {
                const opts = options ?? {}
                const result: GetManifestItemsResult = {}
                if (_.isEmpty(opts)) return result
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) ?? {}
                if (!editorElement) return result
                const {elements, presets: compPresets} = editorElement
                const overrides = Object.fromEntries(
                    Object.entries(opts).map(([key]) => [
                        key,
                        EXTRACT_CONFIG[key as SupportedManifestSpaces].getOverride(extensionAPI, compPointer, [])
                    ])
                )
                const currentPreset = (extensionAPI as PresetsExtensionAPI).presets.get(compPointer as CompRef)
                const rootPresetData = currentPreset && compPresets?.[currentPreset.presetName]?.presetDefaults
                const extractedData = extractManifestItemsFromOverrides(opts, editorElement, overrides, rootPresetData)
                for (const key of Object.keys(extractedData) as SupportedManifestSpaces[]) {
                    const value = extractedData[key] ?? []
                    ;(result[key] ??= []).push(...value)
                }
                if (withInnerElements && elements) {
                    const walkInnerElements = createInnerElementsWalker(opts)
                    walkInnerElements(compPointer, elements, [], flattenPresets(compPresets), result)
                }
                return result
            }

            const getInnerElementSlotType = (
                compRef: CompRef,
                innerElementPath: InnerElementPath,
                slotKeyName: string
            ): string => {
                const containerSlots =
                    getManifestItemsInternal(compRef, true, {
                        data: {types: [{prop: 'container'}]}
                    }).data ?? []
                const container = (() => {
                    for (const conatinerManifestInfo of containerSlots) {
                        if (!_.isEqual(innerElementPath, conatinerManifestInfo.path)) {
                            continue
                        }
                        const isStaticSlot = conatinerManifestInfo.prop === slotKeyName
                        const isDynamicSlot = conatinerManifestInfo.value === slotKeyName
                        if (isStaticSlot || isDynamicSlot) {
                            return conatinerManifestInfo
                        }
                    }
                    return null
                })()
                if (!container) {
                    throw new ReportableError({
                        errorType: 'getInnerElementSlotType_slotNotFound',
                        message: 'Slot type not found for the provided slot name'
                    })
                }
                return _.get(container, ['metaData', 'containerType'])
            }

            const extractProps = <
                T extends CssProperties | CssCustomProperties,
                K extends keyof (T extends CssCustomPropertyItem ? CssCustomPropertyItem : CssPropertyItem)
            >(
                obj: T,
                propertyName: K
            ): Readonly<
                Record<string, (T extends CssCustomPropertyItem ? CssCustomPropertyItem : CssPropertyItem)[K]>
            > => {
                return Object.freeze(
                    Object.fromEntries(
                        Object.entries(obj ?? {}).map(([key, value]) => [key, (value as any)[propertyName]])
                    )
                )
            }

            const inflateInnerElementsPath = (
                innerElementsPath: string[] = [],
                prefix = ['elements'],
                suffix = ['inlineElement']
            ): string[] => innerElementsPath.flatMap((element: string) => [...prefix, element, ...suffix])

            const getCustomCssPropertiesRoles = (
                compPointer: CompRef,
                innerElementsPath?: string[]
            ): Readonly<Record<string, CssPropertyEnumCssPropertyType>> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const path: string[] = inflateInnerElementsPath(innerElementsPath)

                return extractProps<CssCustomProperties, keyof CssCustomPropertyItem>(
                    _.get(editorElement, [...path, 'cssCustomProperties'], {}) satisfies CssCustomProperties,
                    'cssPropertyType'
                )
            }

            const getDefaultAccordingToAppliedPresets = (
                compPointer: CompRef,
                innerElementsPath: string[] = []
            ): Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const defaultsFromPresets = []
                const compPreset = (extensionAPI as PresetsExtensionAPI).presets.get(compPointer)?.presetName
                const inflatedInnerElementPresetPath = inflateInnerElementsPath(innerElementsPath, ['elements'], [])
                const currentInnerElementPath = []

                if (compPreset) {
                    const {cssProperties, cssCustomProperties} = _.get(
                        editorElement,
                        ['presets', compPreset, 'presetDefaults', ...inflatedInnerElementPresetPath],
                        {}
                    ) satisfies PresetInnerElementDefaults
                    defaultsFromPresets.push({
                        cssProperties: extractProps(cssProperties, 'defaultValue'),
                        cssCustomProperties: extractProps(cssCustomProperties, 'defaultValue')
                    })
                }

                for (const innerElement of innerElementsPath) {
                    currentInnerElementPath.push(innerElement)
                    const inflatedInnerElementPath = inflateInnerElementsPath(currentInnerElementPath)
                    inflatedInnerElementPresetPath.splice(0, 2)
                    const elementPreset = (
                        extensionAPI as InnerElementsExtensionAPI
                    ).innerElements.getInnerElementPresets(compPointer, currentInnerElementPath)?.presetName

                    if (!elementPreset) {
                        continue
                    }

                    const {cssProperties, cssCustomProperties} = _.get(
                        editorElement,
                        [
                            ...inflatedInnerElementPath,
                            'presets',
                            elementPreset,
                            'presetDefaults',
                            ...inflatedInnerElementPresetPath
                        ],
                        {}
                    ) satisfies PresetInnerElementDefaults

                    defaultsFromPresets.push({
                        cssProperties: extractProps(cssProperties, 'defaultValue'),
                        cssCustomProperties: extractProps(cssCustomProperties, 'defaultValue')
                    })
                }

                return _.defaultsDeep({}, ...defaultsFromPresets)
            }

            const getDefaultDefinition = (
                compPointer: CompRef,
                innerElementsPath: string[] = []
            ): Readonly<Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'>> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const inflatedInnerElementsPath: string[] = inflateInnerElementsPath(innerElementsPath)
                const defaultsFromPresets = getDefaultAccordingToAppliedPresets(compPointer, innerElementsPath)
                const elementDefaults = {
                    cssProperties: extractProps(
                        _.get(
                            editorElement,
                            [...inflatedInnerElementsPath, 'cssProperties'],
                            {}
                        ) satisfies CssProperties,
                        'defaultValue'
                    ),
                    cssCustomProperties: extractProps(
                        _.get(
                            editorElement,
                            [...inflatedInnerElementsPath, 'cssCustomProperties'],
                            {}
                        ) satisfies CssCustomProperties,
                        'defaultValue'
                    )
                }
                const {cssProperties, cssCustomProperties} = _.defaultsDeep(
                    defaultsFromPresets,
                    elementDefaults
                ) satisfies Pick<BuilderStyle, 'cssCustomProperties' | 'cssProperties'>

                return Object.freeze({
                    cssProperties: Object.freeze(cssProperties),
                    cssCustomProperties: Object.freeze(cssCustomProperties)
                })
            }

            const buildTraversableFlatMap = (compPointer: CompRef): Record<string, {innerElements: string[]}> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const map: Record<string, {innerElements: string[]}> = {}

                const traverse = (current: Record<string, ElementItem> | ElementItem | undefined, path: string[]) => {
                    const currentPathKey = path.join('.')
                    map[currentPathKey] = {
                        innerElements: []
                    }

                    if (!current || typeof current !== 'object') {
                        return
                    }

                    for (const [key, value] of Object.entries(current)) {
                        const newPath = [...path, key]
                        const newPathKey = newPath.join('.')

                        map[currentPathKey].innerElements.push(newPathKey)

                        traverse(value?.inlineElement?.elements, newPath)
                    }
                }

                traverse(editorElement?.elements, [compPointer.id])
                return map
            }

            const findMatchingContainer = (
                slottedComponent: Pointer,
                slotName: string,
                nestedDataPath: NestedDataPathSegment[] = [],
                innerElementPath: InnerElementPath = []
            ): any | null => {
                const allContainers =
                    getManifestItemsInternal(slottedComponent as CompRef, true, {
                        data: {types: [{prop: 'container'}]}
                    }).data ?? []

                const isNestedDataPathMatch = (
                    containerNestedDataPath: NestedDataPathSegment[] | undefined
                ): boolean => {
                    const normalizedContainerPath = containerNestedDataPath ?? []

                    if (normalizedContainerPath.length !== nestedDataPath.length) return false

                    return normalizedContainerPath.every((containerSegment, index) => {
                        const providedSegment = nestedDataPath[index]
                        return (
                            containerSegment.key === providedSegment.key &&
                            containerSegment.type === providedSegment.type
                        )
                    })
                }

                const matchingContainer = allContainers.find(
                    ({prop, path, nestedDataPath: containerNestedDataPath}: any) => {
                        const isPropMatch = prop === slotName
                        const normalizedPath = path ?? []
                        const isPathMatch = _.isEqual(normalizedPath, innerElementPath)

                        return isPropMatch && isPathMatch && isNestedDataPathMatch(containerNestedDataPath)
                    }
                )

                if (allContainers.length > 0 && !matchingContainer) {
                    throw new Error(`Container with the prop ${slotName} does not exist`)
                }

                return matchingContainer
            }

            const validateContainerTypeForPopulation = (
                slottedComponent: Pointer,
                slotName: string,
                nestedDataPath: NestedDataPathSegment[] = [],
                innerElementPath: InnerElementPath = []
            ) => {
                const slottedComponentType = components.getComponentType(slottedComponent)
                if (schemaAPI.isSchemaCollectionRegistered(slottedComponentType)) {
                    const allContainers =
                        getManifestItemsInternal(slottedComponent as CompRef, true, {
                            data: {types: [{prop: 'container'}]}
                        }).data ?? []

                    const isNestedDataPathMatch = (
                        containerNestedDataPath: NestedDataPathSegment[] | undefined
                    ): boolean => {
                        const normalizedContainerPath = containerNestedDataPath ?? []

                        if (normalizedContainerPath.length !== nestedDataPath.length) return false

                        return normalizedContainerPath.every((containerSegment, index) => {
                            const providedSegment = nestedDataPath[index]
                            return (
                                containerSegment.key === providedSegment.key &&
                                containerSegment.type === providedSegment.type
                            )
                        })
                    }

                    const matchingContainer = allContainers.find(
                        ({prop, path, nestedDataPath: containerNestedDataPath}: any) => {
                            const isPropMatch = prop === slotName
                            const normalizedPath = path ?? []
                            const isPathMatch = _.isEqual(normalizedPath, innerElementPath)

                            return isPropMatch && isPathMatch && isNestedDataPathMatch(containerNestedDataPath)
                        }
                    )

                    if (allContainers.length > 0 && !matchingContainer) {
                        throw new Error(`Container with the prop ${slotName} does not exist`)
                    }

                    if (matchingContainer) {
                        const isInvalidContainerType = ['placeholder', 'template', 'simple'].includes(
                            matchingContainer.metaData?.containerType
                        )

                        if (isInvalidContainerType) {
                            throw new Error('Cannot populate to container types placeholder, template, or simple')
                        }
                    }
                }
            }

            const validateContainerTypeForRemove = (
                slottedComponent: Pointer,
                slotName: string,
                nestedDataPath: NestedDataPathSegment[] = [],
                innerElementPath: InnerElementPath = []
            ) => {
                const matchingContainer = findMatchingContainer(
                    slottedComponent,
                    slotName,
                    nestedDataPath,
                    innerElementPath
                )

                if (matchingContainer) {
                    const containerType = matchingContainer.metaData?.containerType

                    if (containerType !== 'slot') {
                        throw new Error(
                            `Cannot remove from container type '${containerType}'. Only 'slot' type containers support removal`
                        )
                    }
                }
            }

            const getSizingType = (compPointer: CompRef, prop: 'width' | 'height'): SizingType | undefined => {
                const currentPreset: BuilderPresets | undefined = (extensionAPI as PresetsExtensionAPI).presets.get(
                    compPointer
                )
                const componentType = components.getComponentType(compPointer)
                const {installation, editorElement} = (getComponentDetailsFromCSM(componentType) ??
                    {}) as EditorReactComponent
                const presetName = currentPreset?.presetName ?? installation?.presets?.large
                const preset = presetName ? editorElement?.presets?.[presetName] : undefined

                return preset?.initialSize?.[prop]?.sizingType ?? installation?.initialSize?.[prop]?.sizingType
            }

            const isContentHeight = (compPointer: CompRef): boolean =>
                getSizingType(compPointer, 'height') === LAYOUT.SIZING_TYPE.content

            const isContentWidth = (compPointer: CompRef): boolean =>
                getSizingType(compPointer, 'width') === LAYOUT.SIZING_TYPE.content

            const isStretchedHeight = (compPointer: CompRef): boolean =>
                getSizingType(compPointer, 'height') === LAYOUT.SIZING_TYPE.stretched

            const isStretchedWidth = (compPointer: CompRef): boolean =>
                getSizingType(compPointer, 'width') === LAYOUT.SIZING_TYPE.stretched

            const shouldPreserveAspectRatio = (compPointer: CompRef): boolean => {
                const componentType = components.getComponentType(compPointer)
                const {installation} = (getComponentDetailsFromCSM(componentType) ?? {}) as EditorReactComponent

                return !!installation?.initialSize?.preserveAspectRatio
            }

            const getContentFill = (compPointer: CompRef): ContentFill | undefined => {
                const compType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(compType) ?? {}
                return editorElement.layout?.contentFill
            }

            const getInitialSize = (compPointer: CompRef): ComponentInitialSize | undefined => {
                const compType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(compType) ?? {}
                return editorElement?.installation?.initialSize
            }

            const hasOverlappingContainerPlugin = (compPointer: CompRef): boolean => {
                const compType = components.getComponentType(compPointer)
                const {editorElement} = (getComponentDetailsFromCSM(compType) ?? {}) as EditorReactComponent
                const {dataItemKey} = editorElement?.layout?.contentFill ?? {}

                return !!(dataItemKey && editorElement!.data![dataItemKey].dataType === 'container')
            }

            const getMainTextField = (componentType: string, data: any): string | undefined => {
                const mainTextProperty = MAIN_TEXT_FIELD_MAPPING[componentType]?.[0]
                if (mainTextProperty) {
                    const path = mainTextProperty.elementPath === 'root' ? [] : mainTextProperty.elementPath.split('.')
                    path.push(mainTextProperty.key)
                    const mainText = _.get(data, path)
                    if (mainText) {
                        return mainText.text ?? mainText
                    }
                }
            }

            return {
                manifest: {
                    getArchType(componentType: string): Archetype {
                        const {editorElement} = getComponentDetailsFromCSM(componentType) ?? {}
                        return editorElement?.archetype ?? ARCHETYPE.UNKNOWN_Archetype
                    },
                    getDisplayName(componentType: string) {
                        const {displayName} = getComponentDetailsFromCSM(componentType)?.editorElement ?? {}
                        return displayName
                    },
                    getManifestItemsFromSeveralSpaces(
                        compPointer: Pointer,
                        options: GetManifestItemsOptions,
                        withInnerElements: boolean = false
                    ) {
                        return getManifestItemsInternal(compPointer as CompRef, withInnerElements, options)
                    },
                    getStyle(
                        compPointer: Pointer,
                        withInnerElements: boolean = false,
                        types?: {prop: string; extract?(data: any): any}[]
                    ) {
                        return (
                            getManifestItemsInternal(compPointer as CompRef, withInnerElements, {style: {types}})
                                .style ?? []
                        )
                    },
                    getData(
                        compPointer: Pointer,
                        withInnerElements: boolean = false,
                        types?: {prop: string; extract?(data: any): any}[]
                    ) {
                        const result = getManifestItemsInternal(compPointer as CompRef, withInnerElements, {
                            data: {types}
                        })
                        return result.data ?? []
                    },
                    cssStyle: {
                        getDefaultDefinition,
                        getCustomCssPropertiesRoles
                    },
                    innerElements: {
                        buildTraversableFlatMap,
                        getInnerElementSlotType
                    },
                    validateContainerTypeForPopulation,
                    validateContainerTypeForRemove,
                    layout: {
                        isContentWidth,
                        isContentHeight,
                        isStretchedWidth,
                        isStretchedHeight,
                        shouldPreserveAspectRatio,
                        getContentFill,
                        getInitialSize
                    },
                    plugins: {
                        hasOverlappingContainerPlugin
                    },
                    getMainTextField
                }
            }
        }
    }
}

export {createExtension, ManifestApi, ManifestExtensionAPI}
